import api from '../config/axiosConfig';

const API_URL = '/inspection-evaluations';

/**
 * Servicio para gestionar evaluaciones de inspección
 */
const inspectionEvaluationService = {
  
  /**
   * Crear evaluación inicial desde un PIPC/Cotización
   * `config` opcional se pasa tal cual a axios (ej. { skipAuthRedirect: true } que usa
   * offlineSyncEngine.js para no disparar el redirect global de sesión vencida a mitad de una
   * sincronización en segundo plano).
   */
  createFromQuotation: async (quotationId, additionalData = {}, config = {}) => {
    try {
      const response = await api.post(
        `${API_URL}/from-quotation/${quotationId}`,
        additionalData,
        config
      );
      return response.data;
    } catch (error) {
      console.error('Error al crear evaluación desde PIPC:', error);
      throw error;
    }
  },

  /**
   * Verificar si existe evaluación para una cotización
   */
  existsByQuotationId: async (quotationId) => {
    try {
      const response = await api.get(`${API_URL}/quotation/${quotationId}/exists`);
      return response.data.exists;
    } catch (error) {
      console.error('Error al verificar existencia de evaluación:', error);
      throw error;
    }
  },

  /**
   * Obtener evaluación por quotation_id
   */
  getByQuotationId: async (quotationId) => {
    try {
      const response = await api.get(`${API_URL}/quotation/${quotationId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener evaluación por quotation_id:', error);
      throw error;
    }
  },

  /**
   * Obtener evaluación por ID
   */
  getById: async (id, config = {}) => {
    try {
      const response = await api.get(`${API_URL}/${id}`, config);
      return response.data;
    } catch (error) {
      console.error('Error al obtener evaluación:', error);
      throw error;
    }
  },

  /**
   * Guardar o actualizar evaluación completa
   */
  save: async (evaluationData, config = {}) => {
    try {
      const response = await api.post(API_URL, evaluationData, config);
      return response.data;
    } catch (error) {
      console.error('Error al guardar evaluación:', error);
      throw error;
    }
  },

  /**
   * Actualizar solo el estado de la evaluación
   */
  updateStatus: async (id, status) => {
    try {
      const response = await api.patch(`${API_URL}/${id}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      throw error;
    }
  },

  /**
   * Obtener todas las evaluaciones de una empresa
   */
  getByCompanyId: async (companyId) => {
    try {
      const response = await api.get(`${API_URL}/company/${companyId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener evaluaciones de empresa:', error);
      throw error;
    }
  },

  /**
   * Eliminar evaluación
   */
  delete: async (id) => {
    try {
      const response = await api.delete(`${API_URL}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar evaluación:', error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas globales
   */
  getStatistics: async () => {
    try {
      const response = await api.get(`${API_URL}/statistics`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
      throw error;
    }
  },

  /**
   * Obtener estado de todos los entes (matriz + sucursales) para una cotización
   */
  getEntitiesStatus: async (quotationId) => {
    try {
      const response = await api.get(`${API_URL}/quotation/${quotationId}/entities`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener estado de entes:', error);
      throw error;
    }
  },

  /**
   * Obtener evaluación de un ente específico (matriz o sucursal)
   * branchId = null para matriz
   */
  getByEntity: async (quotationId, branchId = null, config = {}) => {
    try {
      const params = branchId != null ? `?branchId=${branchId}` : '';
      const response = await api.get(`${API_URL}/quotation/${quotationId}/entity${params}`, config);
      return response.data;
    } catch (error) {
      console.error('Error al obtener evaluación por ente:', error);
      throw error;
    }
  },

  /**
   * Verificar si existe evaluación para un ente específico
   * branchId = null para matriz
   */
  existsByEntity: async (quotationId, branchId = null, config = {}) => {
    try {
      const params = branchId != null ? `?branchId=${branchId}` : '';
      const response = await api.get(`${API_URL}/quotation/${quotationId}/entity/exists${params}`, config);
      return response.data.exists;
    } catch (error) {
      console.error('Error al verificar existencia por ente:', error);
      throw error;
    }
  },

  /**
   * Obtener TODAS las evaluaciones de una cotización (matriz + sucursales)
   */
  getAllByQuotationId: async (quotationId) => {
    try {
      const response = await api.get(`${API_URL}/quotation/${quotationId}/all`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener todas las evaluaciones:', error);
      throw error;
    }
  },

  /**
   * Clonar evaluación a uno o más entes destino
   * targetBranchIds: array de IDs de sucursales destino
   */
  cloneEvaluation: async (sourceEvaluationId, targetBranchIds) => {
    try {
      const response = await api.post(`${API_URL}/${sourceEvaluationId}/clone`, {
        targetBranchIds
      });
      return response.data;
    } catch (error) {
      console.error('Error al clonar evaluación:', error);
      throw error;
    }
  },

  /**
   * Transformar estado del frontend al formato del backend
   */
  transformToBackendFormat: (frontendState, evaluationId, quotationId, companyId, createdBy, branchId = null, siteData = {}) => {
    const concepts = [];
    const textFields = {};

    // Transformar cada sección
    Object.entries(frontendState).forEach(([sectionKey, sectionData]) => {
      const sectionType = getSectionTypeFromKey(sectionKey);
      
      // Transformar conceptos
      if (sectionData.conceptos) {
        Object.entries(sectionData.conceptos).forEach(([conceptKey, conceptData]) => {
          if (conceptData.estado) { // Solo incluir conceptos evaluados
            concepts.push({
              sectionType,
              conceptKey,
              conceptLabel: getConceptLabel(conceptKey),
              estado: conceptData.estado,
              observaciones: conceptData.observaciones || ''
            });
          }
        });
      }

      // Transformar campos de texto
      if (sectionData.camposTexto && Object.keys(sectionData.camposTexto).length > 0) {
        const hasValues = Object.values(sectionData.camposTexto).some(v => v && v.trim() !== '');
        if (hasValues) {
          textFields[sectionType] = sectionData.camposTexto;
        }
      }
    });

    return {
      id: evaluationId,
      quotationId,
      companyId,
      branchId,
      createdBy,
      // Sin status: el backend conserva el actual y lo cambia solo según el avance. Antes se
      // mandaba IN_PROGRESS fijo y cada guardado de una evaluación completa la "re-completaba".
      concepts,
      textFields,
      // Datos "de sitio": sustancias peligrosas y visto bueno de protección civil —
      // heredados de la cotización al crear, editables desde aquí. La capacidad de gas del
      // PIPC ya no se captura aparte: se deriva de "Gas LP" dentro de hazardousSubstances.
      civilProtectionApproval: siteData.civilProtectionApproval || null,
      civilProtectionApprovalDate: siteData.civilProtectionApprovalDate || null,
      civilProtectionApprovalValidity: siteData.civilProtectionApprovalValidity || null,
      noHazardousSubstances: !!siteData.noHazardousSubstances,
      hazardousSubstances: (siteData.noHazardousSubstances ? [] : (siteData.hazardousSubstances || []))
        .filter(item => item.substanceName && item.quantity !== '' && item.unit)
        .map(item => ({
          id: item.id,
          substanceName: item.substanceName,
          quantity: item.quantity,
          unit: item.unit
        }))
    };
  },

  /**
   * Transformar datos del backend al formato del frontend
   */
  transformToFrontendFormat: (backendData) => {
    const frontendState = initializeEmptyState();

    // Transformar conceptos
    if (backendData.concepts) {
      backendData.concepts.forEach(concept => {
        const sectionKey = getSectionKeyFromType(concept.sectionType);
        if (frontendState[sectionKey] && frontendState[sectionKey].conceptos) {
          frontendState[sectionKey].conceptos[concept.conceptKey] = {
            estado: concept.estado || '',
            observaciones: concept.observaciones || ''
          };
        }
      });
    }

    // Transformar campos de texto
    if (backendData.textFields) {
      Object.entries(backendData.textFields).forEach(([sectionType, fields]) => {
        const sectionKey = getSectionKeyFromType(sectionType);
        if (frontendState[sectionKey] && frontendState[sectionKey].camposTexto) {
          frontendState[sectionKey].camposTexto = { ...fields };
        }
      });
    }

    return frontendState;
  }
};

// ============================================
// FUNCIONES AUXILIARES
// ============================================

/**
 * Mapeo de claves de sección del frontend a tipos del backend
 */
const getSectionTypeFromKey = (sectionKey) => {
  const mapping = {
    'estructural': 'ESTRUCTURAL',
    'equipoEspecifico': 'EQUIPO_ESPECIFICO',
    'instalacionElectrica': 'INSTALACION_ELECTRICA',
    'instalacionGas': 'INSTALACION_GAS',
    'instalacionHidrosanitaria': 'INSTALACION_HIDROSANITARIA',
    'sistemasVentilacion': 'SISTEMAS_VENTILACION',
    'poblacion': 'POBLACION',
    'recipientesPresion': 'RECIPIENTES_PRESION',
    'tanquesAlmacenamiento': 'TANQUES_ALMACENAMIENTO'
  };
  return mapping[sectionKey] || sectionKey.toUpperCase();
};

/**
 * Mapeo inverso: tipos del backend a claves del frontend
 */
const getSectionKeyFromType = (sectionType) => {
  const mapping = {
    'ESTRUCTURAL': 'estructural',
    'EQUIPO_ESPECIFICO': 'equipoEspecifico',
    'INSTALACION_ELECTRICA': 'instalacionElectrica',
    'INSTALACION_GAS': 'instalacionGas',
    'INSTALACION_HIDROSANITARIA': 'instalacionHidrosanitaria',
    'SISTEMAS_VENTILACION': 'sistemasVentilacion',
    'POBLACION': 'poblacion',
    'RECIPIENTES_PRESION': 'recipientesPresion',
    'TANQUES_ALMACENAMIENTO': 'tanquesAlmacenamiento'
  };
  return mapping[sectionType] || sectionType.toLowerCase();
};

/**
 * Obtener etiqueta legible de un concepto
 */
const getConceptLabel = (conceptKey) => {
  const labels = {
    // Estructural
    'ventanales': 'Ventanales',
    'anunciosVolados': 'Anuncios Volados',
    'anunciosPanorámicos': 'Anuncios Panorámicos',
    'marquesinas': 'Marquesinas',
    'acabadosFachada': 'Acabados de la Fachada',
    'pretilesBalcones': 'Pretiles de Balcones',
    'inclinacionInmueble': 'Inclinación del Inmueble',
    'cimentacion': 'Cimentación',
    'columnas': 'Columnas',
    'muros': 'Muros',
    'bardasMallas': 'Bardas, Mallas o Cercas',
    'escaleras': 'Escaleras',
    'arboles': 'Árboles',
    'rampasAuto': 'Rampas para Auto',
    'pasosDesnivel': 'Pasos a Desnivel para Vehículos',
    'estacionamiento': 'Estacionamiento',
    
    // Equipo Específico
    'bombas': 'Bombas',
    'moduloDespacho': 'Módulo de Despacho',
    'parosEmergencia': 'Paros de Emergencia',
    'mangueraDescarga': 'Manguera de Descarga',
    'montacargas': 'Montacargas',
    
    // Instalación Eléctrica
    'transformadorElectrico': 'Transformador Eléctrico',
    'subestacionElectrica': 'Subestación Eléctrica',
    'postesDistribucion': 'Postes de Distribución',
    'tablerosElectricos': 'Tableros Eléctricos',
    'equiposComputo': 'Equipos de Cómputo',
    'socketsFocos': 'Sockets y Focos',
    'tomaCorrientes': 'Toma Corrientes',
    'plantaEmergencia': 'Planta de Emergencia',
    'aireAcondicionado': 'Aire Acondicionado',
    
    // Instalación de Gas
    'tuberiaGas': 'Tubería de Gas',
    'tanqueMovil': 'Tanque Móvil',
    'tanqueEstacionario': 'Tanque Estacionario',
    'valvulasTanque': 'Válvulas del Tanque',
    
    // Instalación Hidrosanitaria
    'tuberias': 'Tuberías',
    'drenaje': 'Drenaje',
    'mueblesSanitarios': 'Muebles Sanitarios',
    'bombaHidraulica': 'Bomba Hidráulica',
    'tomaSiamesa': 'Toma Siamesa',
    'aljibeDeposito': 'Aljibe o Depósito de Agua',

    // Recipientes a Presión
    'tanquesHidroneumaticos': 'Tanques Hidroneumáticos',
    'calderas': 'Calderas',
    'autotanques': 'Autotanques',
    'evaporadoras': 'Evaporadoras',
    'intercambiadorCalor': 'Intercambiador de Calor',
    'chillerCondensador': 'Chiller/Condensador',
    'compresor': 'Compresor',
    'otro': 'Otro',

    // Tanques de Almacenamiento
    'gasolina': 'Gasolina',
    'diesel': 'Diésel',
    'gasLP': 'Gas LP',
    'solventes': 'Solventes',
    'pinturas': 'Pinturas',
    'amoniaco': 'Amoniaco',
    'gasNatural': 'Gas Natural',
    'aceiteVehicular': 'Aceite Vehicular',

    // Fila opcional en las secciones de equipos
    'otroEquipo': 'Otro equipo presente',

    // Claves anteriores
    'tuberiaAgua': 'Tubería de Agua',
    'bombaAgua': 'Bomba de Agua',
    'cisterna': 'Cisterna',
    'tinaco': 'Tinaco',
    'drenajeInterior': 'Drenaje Interior',
    'drenajeExterior': 'Drenaje Exterior',
    
    // Sistemas de Ventilación
    'extractores': 'Extractores',
    'ventiladores': 'Ventiladores',
    'ductos': 'Ductos de Ventilación',
    'campanas': 'Campanas de Extracción',
    
    // Población
    'estadoSalud': 'Estado de Salud',
    'estadoPsicologico': 'Estado Psicológico',
    'trabajadoresDiscapacidad': '¿Cuentan con personal discapacitado?',
    'adaptacionDiscapacidad': 'Adaptación para personas con discapacidad',
    
    // Recipientes a Presión
    'compresoresAire': 'Compresores de Aire',
    'calderasVapor': 'Calderas de Vapor',
    'autoclaves': 'Autoclaves',
    'recipientesGas': 'Recipientes de Gas',
    
    // Tanques de Almacenamiento
    'tanquesCombustible': 'Tanques de Combustible',
    'tanquesQuimicos': 'Tanques de Químicos',
    'tanquesAgua': 'Tanques de Agua'
  };
  return labels[conceptKey] || conceptKey;
};

/**
 * Inicializar estado vacío del frontend
 */
const initializeEmptyState = () => ({
  estructural: { conceptos: {}, camposTexto: {} },
  equipoEspecifico: { conceptos: {}, camposTexto: {} },
  instalacionElectrica: { conceptos: {}, camposTexto: {} },
  instalacionGas: { conceptos: {}, camposTexto: {} },
  instalacionHidrosanitaria: { conceptos: {}, camposTexto: {} },
  sistemasVentilacion: { conceptos: {}, camposTexto: {} },
  poblacion: { conceptos: {}, camposTexto: {} },
  recipientesPresion: { conceptos: {}, camposTexto: {} },
  tanquesAlmacenamiento: { conceptos: {}, camposTexto: {} }
});

export default inspectionEvaluationService;
